import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import axios from 'helpers/api';
import * as mapYourMedsConstant from '../../common/mapYourMedsConstant'

const schema = yup.object({
  name: yup.string().trim().required('Name is required'),
  description: yup.string().nullable(),
  is_active: yup.boolean(),
  address1: yup.string().nullable(),
  address2: yup.string().nullable(),
  city: yup.string().nullable(),
  state: yup.string().nullable(),
  zip: yup.string().nullable(),
  phone: yup.string().nullable(),
  email: yup.string().email('Invalid email').nullable()
});

const normalizeAvailability = data => {
  if (typeof data?.available === 'boolean') return data.available;
  if (typeof data?.is_available === 'boolean') return data.is_available;
  if (typeof data?.status === 'boolean') return data.status;
  if (typeof data?.data?.available === 'boolean') return data.data.available;
  if (typeof data?.data?.is_available === 'boolean') return data.data.is_available;

  return false;
};

const DayProgramForm = ({ dayProgram, show, setShow, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const [isNameAvailable, setIsNameAvailable] = useState(null);
  const [stateData, setStateData] = useState(null);
  const [stateoptions] = useState([{ key: 'Select a state', value: '' },
      ...mapYourMedsConstant.STATE_VALUES]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      is_active: true,
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: ''
    }
  });

  const name = watch('name');
  const isEditMode = !!dayProgram?.id;
  const originalName = dayProgram?.name || '';

  // useEffect(() => {
  //   reset({
  //     name: dayProgram?.name || '',
  //     description: dayProgram?.description || '',
  //     is_active: dayProgram ? dayProgram.is_active : true,
  //     address1: dayProgram?.address1 || '',
  //     address2: dayProgram?.address2 || '',
  //     city: dayProgram?.city || '',
  //     state: dayProgram?.state || '',
  //     zip: dayProgram?.zip || '',
  //     phone: dayProgram?.phone || '',
  //     email: dayProgram?.email || ''
  //   });
  //   setIsNameAvailable(null);
  //   setCheckingName(false);
  // }, [dayProgram, reset, show]);

  useEffect(() => {
  if (!show) return; // only reset when modal opens
  reset({
    name: dayProgram?.name || '',
    description: dayProgram?.description || '',
    is_active: dayProgram ? Boolean(dayProgram.is_active) : true,
    address1: dayProgram?.address1 || '',
    address2: dayProgram?.address2 || '',
    city: dayProgram?.city || '',
    state: dayProgram?.state || '',
    zip: dayProgram?.zip || '',
    phone: dayProgram?.phone || '',
    email: dayProgram?.email || ''
  });
  setStateData(dayProgram?.state || '');
  setIsNameAvailable(null);
  setCheckingName(false);
}, [dayProgram?.id, show]); 

  useEffect(() => {
    const trimmedName = name?.trim();

    if (!show || !trimmedName) {
      setIsNameAvailable(null);
      setCheckingName(false);
      return undefined;
    }

    if (isEditMode && trimmedName.toLowerCase() === originalName.toLowerCase()) {
      setIsNameAvailable(true);
      setCheckingName(false);
      return undefined;
    }

    setCheckingName(true);
    const timeoutId = setTimeout(async () => {
      try {
        const tokenStr = localStorage.getItem('accessToken');
        const { data } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/day-programs/check-name`,
          {
            params: { name: trimmedName },
            headers: { Authorization: `Bearer ${tokenStr}` }
          }
        );

        setIsNameAvailable(normalizeAvailability(data));
      } catch (error) {
        setIsNameAvailable(false);
      } finally {
        setCheckingName(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [isEditMode, name, originalName, show]);

  const onSubmit = async formData => {
    if (!isEditMode && isNameAvailable !== true) {
      toast.error('Please use an available Day Program name');
      return;
    }

    if (isNameAvailable === false) {
      toast.error('Day Program name is already in use');
      return;
    }

    setSaving(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      const payload = {
        name: formData.name.trim(),
        description: formData.description || '',
        address1: formData.address1 || '',
        address2: formData.address2 || '',
        city: formData.city || '',
        state: stateData || '',
        zip: formData.zip || '',
        phone: formData.phone || '',
        email: formData.email || '',
        ...(isEditMode && { is_active: formData.is_active ? 1 : 0 }),
      };
      const config = { headers: { Authorization: `Bearer ${tokenStr}` } };

      if (isEditMode) {
        await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/day-programs/update/${dayProgram.id}`,
          payload,
          config
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/day-programs/create`,
          payload,
          config
        );
      }

      toast.success(`Day Program ${isEditMode ? 'updated' : 'created'} successfully`);
      setShow(false);
      onSaved();
    } catch (error) {
      toast.error(`Unable to ${isEditMode ? 'update' : 'create'} day program`);
    } finally {
      setSaving(false);
    }
  };

  const nameFeedback =
    checkingName ? (
      <div className="text-600 fs-10 mt-1">
        <Spinner animation="border" size="sm" className="me-2" />
        Checking name...
      </div>
    ) : isNameAvailable === true ? (
      <div className="text-success fs-10 mt-1">
        <FontAwesomeIcon icon="check" className="me-1" />
        Name is available
      </div>
    ) : isNameAvailable === false ? (
      <div className="text-danger fs-10 mt-1">
        <FontAwesomeIcon icon="times-circle" className="me-1" />
        Name is already in use
      </div>
    ) : null;

  return (
    <Modal show={show} onHide={() => setShow(false)} size="lg">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {isEditMode ? 'Edit Day Program' : 'Create Day Program'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="required">Name</Form.Label>
            <Form.Control
              type="text"
              isInvalid={!!errors.name || isNameAvailable === false}
              isValid={isNameAvailable === true && !errors.name}
              {...register('name')}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
            {nameFeedback}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={3} {...register('description')} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address 1</Form.Label>
            <Form.Control type="text" {...register('address1')} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address 2</Form.Label>
            <Form.Control type="text" {...register('address2')} />
          </Form.Group>

          <Row className="mb-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>City</Form.Label>
                <Form.Control type="text" {...register('city')} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>State</Form.Label>
                {/* <Form.Control type="text" {...register('state')} /> */}
                <Form.Select
                  name="state"
                  value={stateData}
                  onChange={(e) => setStateData(e.target.value)}
                >
                  {stateoptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.key}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Zip</Form.Label>
                <Form.Control type="text" {...register('zip')} />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  {...register('phone')}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  isInvalid={!!errors.email}
                  {...register('email')}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {isEditMode && (
            <Form.Check
              type="switch"
              id="day-program-status"
              label="Active"
              {...register('is_active')}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={
              saving ||
              checkingName ||
              isNameAvailable === false ||
              (!isEditMode && isNameAvailable !== true)
            }
          >
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DayProgramForm;